import React, { useEffect, useMemo, useState } from "react";
import { api, uploadFile, getJson, errorMsg } from "../api";

export default function StepBom({ projectId, onSuccess, uploadedName }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [editingKey, setEditingKey] = useState(null);

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingConfirm, setLoadingConfirm] = useState(false);

  const [pcbQty, setPcbQty] = useState(1);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [parseInfo, setParseInfo] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const data = await getJson(`/user/projects/${projectId}/`);
        if (data?.quantity) setPcbQty(Number(data.quantity));
      } catch (_) {}
    })();
  }, [projectId]);

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function waitForBomParsed({ tries = 30, delay = 1200 } = {}) {
    for (let i = 0; i < tries; i++) {
      const st = await getJson(`/user/projects/${projectId}/bom-status/`);
      if (!st.exists) throw new Error("Aucun upload BOM trouvé pour ce projet.");
      if (st.status === "parsed") return st;
      if (st.status === "error") throw new Error(st.error_message || "Erreur parsing BOM.");
      await sleep(delay);
    }
    throw new Error("Le parsing BOM prend trop de temps. Réessaye dans quelques secondes.");
  }

  const doPreview = async () => {
    setError("");
    setOk("");
    setPreview(null);
    setParseInfo(null);
    setMapping({});
    setEditingKey(null);

    if (!file) return setError("Choisis un fichier BOM (.csv/.txt/.xls/.xlsx/.xlsm).");

    setLoadingPreview(true);
    try {
      const data = await uploadFile(`/user/projects/${projectId}/bom-preview/`, file);
      setPreview(data);

      setMapping(data?.suggested_mapping || {});
      const previewDnp = Number(data?.skipped?.dnp || 0);
      if (previewDnp > 0) {
        setOk(`Aperçu OK. ${previewDnp} ligne${previewDnp > 1 ? "s" : ""} DNP dans le fichier BOM seront retirée${previewDnp > 1 ? "s" : ""} de l'assemblage.`);
      } else {
        setOk("Aperçu OK. Clique sur les entêtes du tableau pour mapper les colonnes.");
      }
    } catch (e) {
      setError(errorMsg(e, "Erreur aperçu BOM"));
    } finally {
      setLoadingPreview(false);
    }
  };

  const doConfirm = async () => {
    setError("");
    setOk("");

    if (!file) return setError("Choisis un fichier BOM (CSV/XLSX).");
    if (!preview) return setError("Fais d'abord l'aperçu du mapping.");

    if (!mapping?.mpn || !mapping?.qty) {
      return setError("Mapping invalide : il faut au minimum MPN et Qty.");
    }

    setLoadingConfirm(true);
    try {
      const qty = Math.max(1, parseInt(pcbQty, 10) || 1);
      try {
        await api.patch(`/user/projects/${projectId}/quantity/`, { quantity: qty });
      } catch (_) {}

      const res = await uploadFile(`/user/projects/${projectId}/bom-confirm/`, file, {
        mapping: JSON.stringify(mapping),
      });
      setParseInfo(res);

      setOk("Mapping confirmé. Analyse en cours...");

      const st =
        res?.status === "parsed"
          ? await getJson(`/user/projects/${projectId}/bom-status/`)
          : await waitForBomParsed();

      setOk(`BOM analysée (${st.parsed_items_count || 0} éléments).`);
      onSuccess({ upload: res, status: st, pcbQty: qty }, file?.name);
    } catch (e) {
      setError(errorMsg(e, "Erreur confirmation/analyse BOM"));
    } finally {
      setLoadingConfirm(false);
    }
  };

  const headers = preview?.headers || [];
  const sample = preview?.sample_rows || [];
  const previewRows = preview?.raw_rows?.length ? preview.raw_rows.slice(0, sample.length || 15) : sample;

  const mappingFields = useMemo(
    () => [
      { key: "mpn", label: "MPN", required: true },
      { key: "qty", label: "Qté", required: true },
      { key: "Designator", label: "Désignateur", required: false },
      { key: "footprint", label: "Empreinte", required: false },
      { key: "manufacturer", label: "Fabricant", required: false },
      { key: "description", label: "Description", required: false },
    ],
    []
  );

  const setMap = (keyName, colName) => setMapping((p) => ({ ...p, [keyName]: colName || "" }));
  const isMappedOk = !!mapping?.mpn && !!mapping?.qty;

  const closeEditing = () => setEditingKey(null);

  const HeaderCell = ({ field }) => {
    const selected = mapping?.[field.key] || "";
    const isEditing = editingKey === field.key;

    return (
      <th
        className="bom-map-th"
        onClick={() => setEditingKey((prev) => (prev === field.key ? null : field.key))}
        title="Cliquer pour choisir la colonne source"
      >
     <div className="bom-map-head">
  <span className="bom-map-label">
    {field.label}{field.required ? " *" : ""}
  </span>

  <span className={`bom-map-selected ${selected ? "is-set" : "is-empty"}`}>
    {selected || "Non mappé"}
  </span>

  <span className="bom-map-caret">▾</span>
</div>


        {isEditing && (
          <div className="bom-map-pop" onClick={(e) => e.stopPropagation()}>
            <div className="bom-map-pop-title">
              Mapper <b>{field.label}</b>
            </div>

            <select
              className="bom-map-select"
              value={selected}
              onChange={(e) => {
                setMap(field.key, e.target.value);
                setEditingKey(null);
              }}
            >
              <option value="">-- aucun --</option>
              {headers.map((h, idx) => (
                <option key={`${field.key}-${idx}`} value={h}>
                  {h}
                </option>
              ))}
            </select>

            <div className="bom-map-pop-actions">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setMap(field.key, "");
                  setEditingKey(null);
                }}
              >
                Effacer
              </button>
              <button type="button" className="btn primary" onClick={closeEditing}>
                OK
              </button>
            </div>
          </div>
        )}
      </th>
    );
  };

  const norm = (s) =>
    String(s ?? "")
      .trim()
      .toLowerCase()
      .replace(/\u00a0/g, " ")
      .replace(/[\t\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 ]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

const displayValue = (v) => {
  const s = String(v ?? "").trim();
  return s.toLowerCase() === "nan" ? "" : s;
};

const findKeyInsensitive = (obj, wanted) => {
  if (!obj || !wanted) return null;
  const w = norm(wanted);
  return Object.keys(obj).find((k) => norm(k) === w) || null;
};

const cell = (row, key) => {
  const src = mapping?.[key];
  if (src) {
    if (row?.[src] !== undefined && row?.[src] !== null) return displayValue(row[src]);

    const normalizedSrc = norm(src);
    if (row?.[normalizedSrc] !== undefined && row?.[normalizedSrc] !== null) {
      return displayValue(row[normalizedSrc]);
    }

    const realKey = findKeyInsensitive(row, src);
    if (realKey && row?.[realKey] !== undefined && row?.[realKey] !== null) {
      return displayValue(row[realKey]);
    }
  }

  const direct = row?.[key];
  if (direct !== undefined && direct !== null && displayValue(direct) !== "") return displayValue(direct);

  return "";
};

  return (
    <div className="stepper-section" onClick={() => editingKey && closeEditing()}>
      <h2>1) Importer BOM</h2>

      <div className="form-row" style={{ marginBottom: 16 }}>
        <label className="label" style={{ fontWeight: 700 }}>Quantité de cartes à produire *</label>
        <input
          type="number"
          className="input"
          min="1"
          step="1"
          value={pcbQty}
          onChange={(e) => setPcbQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
          style={{ width: 120 }}
        />
        <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
          Nombre de cartes PCB à assembler pour ce projet.
        </div>
      </div>

      <div className="form-row">
        <label className="label">Fichier BOM</label>
        <input
  type="file"
  accept=".csv,.txt,.xls,.xlsx,.xlsm"
  onChange={(e) => setFile(e.target.files?.[0] || null)}
/>

      </div>

      {file ? (
        <p className="muted">
          Fichier sélectionné : <b>{file.name}</b>
        </p>
      ) : uploadedName ? (
        <p className="muted">
          Dernier fichier uploadé : <b>{uploadedName}</b>
        </p>
      ) : null}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="btn" onClick={doPreview} disabled={loadingPreview}>
          {loadingPreview ? "Aperçu..." : "1) Aperçu du mapping"}
        </button>

        <button className="btn primary" onClick={doConfirm} disabled={loadingConfirm || !preview || !isMappedOk}>
          {loadingConfirm ? "Analyse..." : "2) Confirmer & analyser"}
        </button>
      </div>

      {error && <div className="alert error" style={{ marginTop: 12 }}>{error}</div>}
      {ok && <div className="alert ok" style={{ marginTop: 12 }}>{ok}</div>}
      {Number(preview?.skipped?.dnp || 0) > 0 && !parseInfo && (
        <div className="alert ok" style={{ marginTop: 12 }}>
          {preview.skipped.dnp} ligne{Number(preview.skipped.dnp) > 1 ? "s" : ""} DNP dans le fichier BOM seront retirées de l'assemblage.
        </div>
      )}

      {preview && (
        <div style={{ marginTop: 14 }}>
          {!mapping?.Designator && (
            <div className="alert error" style={{ marginTop: 12 }}>
              Designator non mappé : vous risquez de perdre les Designators.
            </div>
          )}

          <h3 style={{ marginTop: 14 }}>Échantillon (clique sur les entêtes pour mapper)</h3>

          <div style={{ marginTop: 12 }}>
            <table className="table bom-preview-table">

              <thead>
                <tr>
                  {mappingFields.map((f) => (
                    <HeaderCell key={f.key} field={f} />
                  ))}
                </tr>
              </thead>
              <tbody>
  {previewRows.map((r, i) => (
    <tr key={i}>
      <td>{cell(r, "mpn")}</td>
      <td>{cell(r, "qty")}</td>
      <td>{cell(r, "Designator")}</td>
      <td>{cell(r, "footprint")}</td>
      <td>{cell(r, "manufacturer")}</td>
      <td>{cell(r, "description")}</td>
    </tr>
  ))}
</tbody>
            </table>
          </div>

          {/* <div className="muted" style={{ marginTop: 10, fontSize: 12 }}>
            ✅ Mapping : <b>MPN</b>={mapping?.mpn || "-"} | <b>Qty</b>={mapping?.qty || "-"} | <b>Designator</b>={mapping?.Designator || "-"}
          </div> */}
        </div>
      )}
    </div>
  );
}

